import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  deleteChecklistGrupo,
  getChecklistById,
  putChecklistGrupo,
} from "../../api/editChecklistApi";
import { ChecklistResponse, ChecklistGrupo } from "../../types/editChecklist";
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { toast } from "react-toastify";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Add, Delete } from "@mui/icons-material";

const GruposChecklist: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const checklistId = Number(id);
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState<ChecklistResponse | null>(null);
  const [grupos, setGrupos] = useState<ChecklistGrupo[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token") || "";
        const data = await getChecklistById(token, checklistId);

        const ordered = [...(data.grupo_perguntas || [])].sort(
          (a, b) =>
            (a.ordem_checklist_grupo ?? 0) - (b.ordem_checklist_grupo ?? 0)
        );
        setChecklist(data);
        setGrupos(ordered);
      } catch (err) {
        console.error(err);
        toast.error("Erro ao buscar grupos");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [checklistId]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const newGrupos = Array.from(grupos);
    const [moved] = newGrupos.splice(result.source.index, 1);
    newGrupos.splice(result.destination.index, 0, moved);

    const updated = newGrupos.map((g, index) => ({
      ...g,
      ordem_checklist_grupo: index + 1,
    }));

    setGrupos(updated);

    try {
      const token = localStorage.getItem("token") || "";
      await Promise.all(
        updated.map((g) =>
          putChecklistGrupo(token, g.id, {
            ...g,
            ordem_checklist_grupo: g.ordem_checklist_grupo,
          })
        )
      );
      toast.success("Ordem atualizada!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao salvar nova ordem");
    }
  };

  const handleDeleteGrupo = async (grupoId: number) => {
    if (!window.confirm("Tem certeza que deseja excluir este grupo?")) return;

    try {
      const token = localStorage.getItem("token") || "";
      await deleteChecklistGrupo(token, grupoId);

      setGrupos((prev) => prev.filter((g) => g.id !== grupoId));

      toast.success("Grupo removido com sucesso!");
    } catch (err) {
      console.error(err);
      toast.error("Erro ao remover grupo");
    }
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" mb={2}>
        Grupos de Perguntas do Checklist #{checklistId}
      </Typography>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="grupos-list">
          {(provided) => (
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{ width: "100%", maxWidth: 700 }}
            >
              {grupos.map((g, index) => (
                <Draggable
                  key={g.id.toString()}
                  draggableId={g.id.toString()}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <Paper
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        mb: 1,
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        background: snapshot.isDragging ? "#f0f0f0" : "white",
                      }}
                    >
                      <Box
                        {...provided.dragHandleProps}
                        sx={{
                          pr: 2,
                          display: "flex",
                          alignItems: "center",
                          color: "gray",
                          cursor: "grab",
                        }}
                      >
                        <DragIndicatorIcon />
                      </Box>
                      <ListItem
                        sx={{ width: "100%" }}
                        secondaryAction={
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              edge="end"
                              aria-label="add-question"
                              onClick={() =>
                                navigate(
                                  `/editarChecklist/grupo/${g.id}/pergunta/novo`
                                )
                              }
                            >
                              <Add />
                            </IconButton>

                            <IconButton
                              edge="end"
                              aria-label="edit"
                              onClick={() =>
                                navigate(`/editarChecklist/grupo/${g.id}`)
                              }
                            >
                              <EditIcon />
                            </IconButton>

                            <IconButton
                              edge="end"
                              aria-label="delete"
                              color="error"
                              onClick={() => handleDeleteGrupo(g.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={`${g.ordem_checklist_grupo}. ${g.descricao_checklist_grupo}`}
                          secondary={`Perguntas: ${g.perguntas?.length || 0}`}
                        />
                      </ListItem>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>

      <Box mt={2} display="flex" gap={2}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate(`/checklist/${checklistId}/grupos/novo`)}
        >
          Adicionar novo grupo de perguntas
        </Button>
      </Box>
    </Box>
  );
};

export default GruposChecklist;
